import { getFinalPolicyId, handleClaimFiling } from '@/core/utils/policy.util';
import { useDashboard } from '@/contexts/dashboard/dashboard.context';
import { useGeneralContext } from '@/contexts/general/general.context';
import {
  getQuickActionExtLink,
  handleDownloadPolicy,
} from '@/core/lib/controllers/policy.cont';
import { DownloadPolicyType } from '@/data/api/policy/types/policy.types';
import { PolicyObject } from '@/data/api/policy/types/policyObject.types';
import { useRouter } from 'next/navigation';
import { useCallback, useRef } from 'react';
import { errorToast } from '@/components/toasts/toast';
import {
  ACTION_MODAL_TYPE,
  QUICK_ACTION_BE_KEYS,
} from '@/core/constants/quickAction.constants';
import { useClevertap } from '@/contexts/analytics/analytics.context';
import { openCsatModal } from '@/utils/csat-rating/openCsatModal';

export const usePolicyDetails = () => {
  const clevertap = useClevertap();
  const { setDownloadPolicyStatus, setDashboardModel, dashboardLabels } =
    useDashboard();
  const { policies: policiesData } = useGeneralContext();
  const router = useRouter();
  const { policies } = useGeneralContext();
  const isDownloading = useRef(false);

  const getPolicyDetails = useCallback(() => {
    const policies = policiesData.value;
    if (policies === null) {
      return {};
    }
    const allActivePolicies = Object.values(policies.active).reduce(
      (acc, curr) => [...acc, ...curr],
      []
    );
    const allExpiredPolicies = Object.values(policies.expired).reduce(
      (acc, curr) => [...acc, ...curr],
      []
    );
    const allUpcomingPolicies = Object.values(policies.upcoming).reduce(
      (acc, curr) => [...acc, ...curr],
      []
    );
    const allCancelledPolicies = Object.values(policies.cancelled).reduce(
      (acc, curr) => [...acc, ...curr],
      []
    );

    const policyDetails: Record<string, PolicyObject[]> = {};
    if (policies?.active && allActivePolicies.length > 0) {
      policyDetails['Active'] = allActivePolicies;
    }
    if (policies?.upcoming && allUpcomingPolicies.length > 0) {
      policyDetails['Upcoming'] = allUpcomingPolicies;
    }
    if (policies?.expired && allExpiredPolicies.length > 0) {
      policyDetails['Expired'] = allExpiredPolicies;
    }
    if (policies?.cancelled && allCancelledPolicies.length > 0) {
      policyDetails['Cancelled'] = allCancelledPolicies;
    }

    return policyDetails;
  }, [policiesData]);

  const policyDownload = useCallback(
    async (
      policyId: string,
      downloadType: DownloadPolicyType,
      productCode: string
    ) => {
      if (isDownloading.current) {
        return;
      }
      setDownloadPolicyStatus('loading');
      try {
        isDownloading.current = true;
        const policy = policies.value?.individual.find(
          (policy) => policy.policyId === policyId
        );
        const response = await handleDownloadPolicy({
          downloadType: downloadType,
          policyId,
          productCode: productCode,
          clevertap,
          policy,
        });

        if (response === 'success') {
          // clevertap?.sendEventWithName('CSAT rating', {
          //   Source:
          //     downloadType === 'policy'
          //       ? 'Email Policy Copy'
          //       : downloadType === 'taxCertificate'
          //         ? '80D Tax Certificate'
          //         : 'Health Card',
          // });
          setDownloadPolicyStatus('success');
          openCsatModal(
            downloadType === 'policy'
              ? 'Email Policy Copy'
              : downloadType === 'taxCertificate'
                ? '80D Tax Certificate'
                : 'Health Card'
          );
        } else {
          setDownloadPolicyStatus('error');
        }
      } catch {
        setDownloadPolicyStatus('failure');
      } finally {
        isDownloading.current = false;
      }
    },
    [setDownloadPolicyStatus]
  );

  const actionClickHandler = useCallback(
    (action: string, policy: PolicyObject) => {
      if (
        action === 'download' ||
        action === 'health_download' ||
        action === 'other_download'
      ) {
        policyDownload(
          getFinalPolicyId(policy.originalPolicyId, policy.policyId),
          'policy',
          policy.policyProduct?.ssProductCode || ''
        );
      } else if (action === 'file') {
        handleClaimFiling(
          policy,
          (path, replace) =>
            replace ? router.replace(path) : router.push(path),
          (val) => {
            if (val === 'assistance') {
              setDashboardModel({
                policyType: policy.policyProduct?.portalLob,
                action: ACTION_MODAL_TYPE.assistFiling,
              });
            } else {
              setDashboardModel({
                policyType: undefined,
                action: ACTION_MODAL_TYPE.digitalFiling,
              });
            }
          },
          dashboardLabels.policyDetailsCms.policyCard.filingLink,
          undefined,
          clevertap,
          'dashboard'
        );
      } else {
        getRedirectionLink('policyAlterations', policy.policyNo);
      }
    },
    [policyDownload, router, clevertap]
  );

  const getRedirectionLink = async (action: string, policyNo: string) => {
    try {
      const link = await getQuickActionExtLink(
        QUICK_ACTION_BE_KEYS[action],
        policyNo
      );
      router.push(link);
    } catch {
      errorToast('Failed to fetch redirection link');
    }
  };

  return { getPolicyDetails, actionClickHandler };
};
